"use client";

import clsx from "clsx";
import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "@repo/next-utils/router";
import { Button } from "@repo/ui/button";
import { Text } from "@repo/ui/text";
import { ChevronLeft } from "@/theme/icons/chevron-left";
import { ChevronRight } from "@/theme/icons/chevron-right";
import { Pause } from "@/theme/icons/pause";
import { Play } from "@/theme/icons/play";
import {
  TYPOGRAPHY_BODY3_BOLD,
  TYPOGRAPHY_LABEL3_BLACK,
  TYPOGRAPHY_SMALLPRINT,
} from "@/theme/topshop/typography/typography-constants";
import { useSmallViewport } from "../viewport";
import type {
  BannerContent,
  BannerCarouselContent,
} from "./global-banner.types";
import styles from "./global-banner.module.css";

type SingleBannerProps = {
  content: BannerContent;
  inCarousel?: boolean;
};

type GlobalBannerProps = {
  content: BannerCarouselContent;
  autoPlayInterval?: number;
};

export const SingleBanner = ({
  content,
  inCarousel = false,
}: SingleBannerProps) => {
  const { desktop, mobile, ctaUrl, theme, code } = content;
  const isMobile = useSmallViewport();

  const config = {
    title: isMobile ? mobile.title : desktop.title,
    subtitle: isMobile ? mobile.subtitle : desktop.subtitle,
    text: isMobile ? mobile.text : desktop.text,
  };

  const isCenteredLayout =
    [config.title, config.subtitle, config.text].filter(Boolean).length <= 1 ||
    (config.title && config.subtitle && !config.text && !code);

  const bannerInner = (
    <div
      className={clsx(
        styles.content,
        isCenteredLayout && styles.contentCentered,
      )}
    >
      {config.title && (
        <Text className={clsx(TYPOGRAPHY_BODY3_BOLD, styles.title)}>
          {config.title}
        </Text>
      )}
      {config.subtitle && (
        <Text className={clsx(TYPOGRAPHY_BODY3_BOLD)}>{config.subtitle}</Text>
      )}
      <Text className={clsx(TYPOGRAPHY_SMALLPRINT, styles.text)}>
        {config.text && config.text}{" "}
        {code && (
          <strong
            className={clsx(styles.codeHighlight, TYPOGRAPHY_LABEL3_BLACK)}
          >
            {code}
          </strong>
        )}
      </Text>
    </div>
  );

  const isClickable = Boolean(ctaUrl?.href);
  const bannerClassName = clsx(
    styles.banner,
    inCarousel && styles.bannerInCarousel,
    !inCarousel && theme && styles[theme],
    isClickable && styles.clickable,
  );

  if (isClickable && ctaUrl?.href) {
    const ariaLabel =
      [config.title, config.subtitle].filter(Boolean).join(" - ") +
      " - Click to learn more";

    return (
      <Link
        href={ctaUrl.href}
        className={bannerClassName}
        aria-label={ariaLabel}
        data-testid="global-banner"
      >
        {bannerInner}
      </Link>
    );
  }

  return (
    <div className={bannerClassName} data-testid="global-banner">
      {bannerInner}
    </div>
  );
};

export const GlobalBanner = ({
  content,
  autoPlayInterval = 5000,
}: GlobalBannerProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const totalBanners = content.length;

  const goToNext = useCallback(() => {
    setDirection("next");
    setCurrentIndex((prev) => (prev + 1) % totalBanners);
  }, [totalBanners]);

  const goToPrevious = useCallback(() => {
    setDirection("prev");
    setCurrentIndex((prev) => (prev - 1 + totalBanners) % totalBanners);
  }, [totalBanners]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isPlaying && totalBanners > 1) {
      timerRef.current = setInterval(goToNext, autoPlayInterval);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying, autoPlayInterval, goToNext, totalBanners]);

  if (content.length === 0) {
    return null;
  }

  if (content.length === 1 && content[0]) {
    return <SingleBanner content={content[0]} />;
  }

  const currentBanner = content[currentIndex];
  const currentTheme = currentBanner?.theme;

  return (
    <div
      className={clsx(styles.carousel, currentTheme && styles[currentTheme])}
      data-testid="global-banner-carousel"
      role="region"
      aria-label="Promotional banner carousel"
      aria-roledescription="carousel"
    >
      <Button
        size="icon"
        onClick={togglePlayPause}
        className={clsx(styles.controlButton, styles.playPauseButton)}
        aria-label={isPlaying ? "Pause carousel" : "Play carousel"}
      >
        {isPlaying ? (
          <Pause label="Pause" className={styles.controlIcon} />
        ) : (
          <Play label="Play" className={styles.controlIcon} />
        )}
      </Button>
      <div className={styles.bannerContainer}>
        <div className={styles.bannerContainerInner}>
          <Button
            size="icon"
            onClick={() => {
              setIsPlaying(false);
              goToPrevious();
            }}
            className={clsx(styles.controlButton, styles.chevronButton)}
            aria-label="Previous banner"
          >
            <ChevronLeft label="Previous" className={styles.controlIcon} />
          </Button>
          <div className={styles.bannerSlideWrapper}>
            {currentBanner && (
              <div
                key={currentIndex}
                className={clsx(
                  styles.bannerSlide,
                  direction === "next" ? styles.slideNext : styles.slidePrev,
                )}
                role="group"
                aria-roledescription="slide"
                aria-label={`Banner ${currentIndex + 1} of ${totalBanners}`}
              >
                <SingleBanner content={currentBanner} inCarousel />
              </div>
            )}
          </div>
          <Button
            size="icon"
            onClick={() => {
              setIsPlaying(false);
              goToNext();
            }}
            className={clsx(styles.controlButton, styles.chevronButton)}
            aria-label="Next banner"
          >
            <ChevronRight label="Next" className={styles.controlIcon} />
          </Button>
        </div>
      </div>
    </div>
  );
};
